import React, { useEffect, useState } from 'react';
import { Modal } from '../ui';
import type { TenantUserRow } from './TenantUsersTable';

type ResetMode = 'TEMP_PASSWORD' | 'RESET_EMAIL';

type ResetAdminPasswordDialogProps = {
  isOpen: boolean;
  admins: TenantUserRow[];
  selectedUserId: string | null;
  isSubmitting?: boolean;
  generatedPassword?: string | null;
  onClose: () => void;
  onSubmit: (payload: { userId: string; mode: ResetMode; newPassword?: string }) => void;
};

export const ResetAdminPasswordDialog: React.FC<ResetAdminPasswordDialogProps> = ({
  isOpen,
  admins,
  selectedUserId,
  isSubmitting,
  generatedPassword,
  onClose,
  onSubmit
}) => {
  const [userId, setUserId] = useState<string>('');
  const [mode, setMode] = useState<ResetMode>('TEMP_PASSWORD');
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setMode('TEMP_PASSWORD');
    setNewPassword('');
    setUserId(selectedUserId || admins[0]?.id || '');
  }, [isOpen, selectedUserId, admins]);

  const hasAdmins = admins.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Resetear clave de administrador">
      <div className="space-y-3">
        {!hasAdmins ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Esta funeraria no tiene administradores. Crea uno antes de resetear credenciales.
          </p>
        ) : (
          <>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Administrador objetivo</label>
              <select
                value={userId}
                aria-label="Administrador objetivo"
                onChange={(e) => setUserId(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                {admins.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.email})
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode('TEMP_PASSWORD')}
                className={`rounded-lg border px-3 py-2 text-sm font-medium ${
                  mode === 'TEMP_PASSWORD'
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                }`}
              >
                Clave temporal
              </button>
              <button
                type="button"
                onClick={() => setMode('RESET_EMAIL')}
                className={`rounded-lg border px-3 py-2 text-sm font-medium ${
                  mode === 'RESET_EMAIL'
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                }`}
              >
                Email de reset
              </button>
            </div>
            {mode === 'TEMP_PASSWORD' && (
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">
                  Clave temporal (opcional)
                </label>
                <input
                  type="password"
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Si lo dejas vacío se genera una segura"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
            )}
            {generatedPassword && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                Clave temporal generada: <span className="font-mono font-semibold">{generatedPassword}</span>
              </div>
            )}
          </>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-slate-100"
          >
            Cerrar
          </button>
          <button
            type="button"
            disabled={!hasAdmins || !userId || isSubmitting}
            onClick={() =>
              onSubmit({
                userId,
                mode,
                newPassword: mode === 'TEMP_PASSWORD' ? newPassword || undefined : undefined
              })
            }
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {isSubmitting ? 'Procesando…' : 'Confirmar reset'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
